import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import {
  ADMIN_MESSAGE_MAX,
  FEEDBACK_COMPLETE_FILTER,
  FEEDBACK_READ_FILTER,
  FEEDBACK_STATUS,
  type FeedbackReadFilter,
} from '../constants/feedback.constants';

export type FeedbackStatus = (typeof FEEDBACK_STATUS)[number];

export const feedbackUserSchema = z.object({
  id: z.coerce.number(),
  fullName: z.string(),
  username: z.string(),
  hrEmpId: z.string(),
  mobile: z.string(),
  thana: z.string().nullable(),
});

export const feedbackTypeSummarySchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  code: z.string(),
});

export const feedbackListItemSchema = z.object({
  id: z.coerce.number(),
  userId: z.coerce.number(),
  user: feedbackUserSchema.nullable(),
  typeId: z.coerce.number(),
  type: feedbackTypeSummarySchema.nullable(),
  message: z.string(),
  adminMessage: z.string().nullable(),
  status: z.enum(FEEDBACK_STATUS),
  feedbackDate: z.coerce.date(),
  enteredAt: z.string().nullable(),
  readAt: z.coerce.date().nullable(),
  readBy: z.coerce.number().nullable(),
  completedAt: z.coerce.date().nullable(),
  completedBy: z.coerce.number().nullable(),
  completedByName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const feedbackListTotalsSchema = z.object({
  complete: z.number(),
  notComplete: z.number(),
  unread: z.number(),
});

export const paginatedFeedbacksSchema = z.object({
  data: z.array(feedbackListItemSchema),
  meta: paginationMetaSchema.extend({ totals: feedbackListTotalsSchema }),
});

export const feedbackSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  dateFrom: z.iso.date().optional().catch(undefined),
  dateTo: z.iso.date().optional().catch(undefined),
  typeId: z.coerce.number().int().positive().optional().catch(undefined),
  reportingToId: z.coerce.number().int().positive().optional().catch(undefined),
  userId: z.coerce.number().int().positive().optional().catch(undefined),
  completeStatus: z.enum(FEEDBACK_COMPLETE_FILTER).optional().catch(undefined),
  readStatus: z.enum(FEEDBACK_READ_FILTER).optional().catch(undefined),
});

export type FeedbackSearch = z.infer<typeof feedbackSearchSchema>;

export interface FeedbackListParams {
  startDate?: string | undefined;
  endDate?: string | undefined;
  typeId?: number | undefined;
  search?: string | undefined;
  reportingToId?: number | undefined;
  userId?: number | undefined;
  completeStatus?: Exclude<FeedbackSearch['completeStatus'], 'all'> | undefined;
  readStatus?: FeedbackReadFilter | undefined;
}

export const completeFeedbackSchema = z.object({
  adminMessage: z
    .string()
    .trim()
    .max(ADMIN_MESSAGE_MAX, `Admin message must be ${ADMIN_MESSAGE_MAX} characters or fewer`)
    .optional(),
});

export type CompleteFeedbackPayload = z.infer<typeof completeFeedbackSchema>;

export type FeedbackUser = z.infer<typeof feedbackUserSchema>;
export type FeedbackTypeSummary = z.infer<typeof feedbackTypeSummarySchema>;
export type FeedbackListItem = z.infer<typeof feedbackListItemSchema>;
export type PaginatedFeedbacks = z.infer<typeof paginatedFeedbacksSchema>;
